import React, { useEffect, useState } from "react";
import Popup from "../../components/Popup/Popup";
import { jsPDF } from "jspdf";
import "./CertificateGenerator.css";

const getImageData = async (url) => {
    const img = new Image();
    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    return { data: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight };
};

const loadPdfRoboto = async (doc) => {
    try {
        const [regularRes, boldRes, italicRes, boldItalicRes] = await Promise.all([
            fetch(`${process.env.PUBLIC_URL}/fonts/Roboto/Roboto-Regular.ttf`),
            fetch(`${process.env.PUBLIC_URL}/fonts/Roboto/Roboto-Bold.ttf`),
            fetch(`${process.env.PUBLIC_URL}/fonts/Roboto/Roboto-Italic.ttf`),
            fetch(`${process.env.PUBLIC_URL}/fonts/Roboto/Roboto-BoldItalic.ttf`),
        ]);

        if (!regularRes.ok || !boldRes.ok || !italicRes.ok || !boldItalicRes.ok) {
            return false;
        }

        const [regularBuffer, boldBuffer, italicBuffer, boldItalicBuffer] = await Promise.all([
            regularRes.arrayBuffer(),
            boldRes.arrayBuffer(),
            italicRes.arrayBuffer(),
            boldItalicRes.arrayBuffer(),
        ]);

        const toBase64 = (buffer) => {
            const bytes = new Uint8Array(buffer);
            let binary = "";
            bytes.forEach((byte) => {
                binary += String.fromCharCode(byte);
            });
            return btoa(binary);
        };

        doc.addFileToVFS("Roboto-Regular.ttf", toBase64(regularBuffer));
        doc.addFileToVFS("Roboto-Bold.ttf", toBase64(boldBuffer));
        doc.addFileToVFS("Roboto-Italic.ttf", toBase64(italicBuffer));
        doc.addFileToVFS("Roboto-BoldItalic.ttf", toBase64(boldItalicBuffer));
        doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
        doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
        doc.addFont("Roboto-Italic.ttf", "Roboto", "italic");
        doc.addFont("Roboto-BoldItalic.ttf", "Roboto", "bolditalic");
        doc.setFont("Roboto", "normal");
        return true;
    } catch {
        return false;
    }
};

const getFontStyle = (bold, italic) => {
    if (bold && italic) {
        return "bolditalic";
    }
    if (bold) {
        return "bold";
    }
    if (italic) {
        return "italic";
    }
    return "normal";
};

const parseStyledSegments = (text) => {
    const segments = [];
    const tokenRegex = /(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let lastIndex = 0;
    let match;

    while ((match = tokenRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ text: text.slice(lastIndex, match.index), bold: false, italic: false });
        }

        const token = match[0];
        if (token.startsWith("***") && token.endsWith("***")) {
            segments.push({ text: token.slice(3, -3), bold: true, italic: true });
        } else if (token.startsWith("**") && token.endsWith("**")) {
            segments.push({ text: token.slice(2, -2), bold: true, italic: false });
        } else {
            segments.push({ text: token.slice(1, -1), bold: false, italic: true });
        }

        lastIndex = tokenRegex.lastIndex;
    }

    if (lastIndex < text.length) {
        segments.push({ text: text.slice(lastIndex), bold: false, italic: false });
    }

    return segments.filter((segment) => segment.text.length > 0);
};

export default function CertificateGenerator({ employee, employeeName, employeeHireDate, employeeDescription }) {
    const [showCertificatePopup, setShowCertificatePopup] = useState(false);
    const [certificateBody, setCertificateBody] = useState("");
    const [previewPdfUrl, setPreviewPdfUrl] = useState("");
    const [previewRevision, setPreviewRevision] = useState(0);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

    const todayDate = new Date().toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "2-digit",
    });
    const issueDate = new Date();
    const issueDay = String(issueDate.getDate()).padStart(2, "0");
    const issueMonth = issueDate.toLocaleDateString("es-CO", { month: "long" });
    const issueMonthCapitalized = issueMonth.charAt(0).toUpperCase() + issueMonth.slice(1);
    const issueYear = issueDate.getFullYear();
    const issueDateLiteral = `${issueDay} dias del mes de ${issueMonthCapitalized} de ${issueYear}`;
    const certificateDateLine = `Bogotá D.C., ${todayDate}`;

    const defaultCertificateBody = `Por medio de la presente me permito informar que **${employeeName}**, identificado con cédula de ciudadanía **${employee?.documentId || "***Agregar Documento***"}** de Bogotá D.C., labora en **Ashara Studios S.A.S** desde el ${employeeHireDate}, desempeñando principalmente el cargo de ${employeeDescription} en modalidad ${employee?.workday || "sin jornada"}, con un contrato de prestación de servicios.\n\nDevenga un salario mensual de ***Salario en letras*** pesos Mcte ***Salario en numeros*** COP.`;

    useEffect(() => {
        if (!showCertificatePopup) {
            if (previewPdfUrl) {
                URL.revokeObjectURL(previewPdfUrl);
                setPreviewPdfUrl("");
            }
            return;
        }

        let cancelled = false;
        let nextUrl = "";
        const timer = setTimeout(async () => {
            setIsGeneratingPreview(true);
            try {
                const doc = await buildCertificatePdf();
                const blob = doc.output("blob");
                nextUrl = URL.createObjectURL(blob);
                if (!cancelled) {
                    setPreviewPdfUrl((prev) => {
                        if (prev) {
                            URL.revokeObjectURL(prev);
                        }
                        return nextUrl;
                    });
                } else if (nextUrl) {
                    URL.revokeObjectURL(nextUrl);
                }
            } catch {
            } finally {
                if (!cancelled) {
                    setIsGeneratingPreview(false);
                }
            }
        }, 200);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (nextUrl) {
                URL.revokeObjectURL(nextUrl);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCertificatePopup, previewRevision, issueDateLiteral]);

    const handleOpenPopup = () => {
        setCertificateBody(defaultCertificateBody);
        setShowCertificatePopup(true);
        setPreviewRevision((prev) => prev + 1);
    };

    const handleRefreshPreview = () => {
        setPreviewRevision((prev) => prev + 1);
    };

    const buildCertificatePdf = async () => {
        const [logoImg, firmaImg, logoHImg] = await Promise.all([
            getImageData(`${process.env.PUBLIC_URL}/img/pdf-logo.png`),
            getImageData(`${process.env.PUBLIC_URL}/img/pdf-firma.JPG`),
            getImageData(`${process.env.PUBLIC_URL}/img/pdf-logo-h.png`),
        ]);
        const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
        const hasRoboto = await loadPdfRoboto(doc);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 84;
        const maxTextWidth = pageWidth - (marginX * 2);
        const centerX = pageWidth / 2;
        let cursorY = 56;
        const sanitizedCertificateBody = certificateBody.replace(/<[^>]*>/g, "");

        const addWrappedLine = (text, size = 12, bold = false, center = false, gap = 20) => {
            doc.setFont(hasRoboto ? "Roboto" : "helvetica", bold ? "bold" : "normal");
            doc.setFontSize(size);

            if (center) {
                const lines = doc.splitTextToSize(text, maxTextWidth);
                lines.forEach((line) => {
                    doc.text(line, centerX, cursorY, { align: "center" });
                    cursorY += gap;
                });
            } else {
                const lines = doc.splitTextToSize(text, maxTextWidth);
                doc.text(lines, marginX, cursorY);
                cursorY += lines.length * gap;
            }
        };

        const addStyledBody = (text, size = 12, lineGap = 18, paragraphGap = 10) => {
            const lines = text.split("\n");
            doc.setFontSize(size);

            const drawStyledLine = (tokens, baseWidth, justify = false) => {
                const spaceCount = tokens.filter((token) => /^\s+$/.test(token.text)).length;
                const extraPerSpace = justify && spaceCount > 0 ? (maxTextWidth - baseWidth) / spaceCount : 0;
                let drawX = marginX;
                tokens.forEach((token) => {
                    const style = getFontStyle(token.bold, token.italic);
                    doc.setFont(hasRoboto ? "Roboto" : "helvetica", style);
                    doc.text(token.text, drawX, cursorY);
                    drawX += doc.getTextWidth(token.text);
                    if (extraPerSpace > 0 && /^\s+$/.test(token.text)) {
                        drawX += extraPerSpace;
                    }
                });
                cursorY += lineGap;
            };

            lines.forEach((line) => {
                if (!line.trim()) {
                    cursorY += paragraphGap;
                    return;
                }

                const segments = parseStyledSegments(line);
                const wordTokens = [];
                segments.forEach((segment) => {
                    const parts = segment.text.split(/(\s+)/);
                    parts.forEach((part) => {
                        if (part) {
                            wordTokens.push({ text: part, bold: segment.bold, italic: segment.italic });
                        }
                    });
                });

                let currentLine = [];
                let currentWidth = 0;
                const wrappedLines = [];

                wordTokens.forEach((token) => {
                    const style = getFontStyle(token.bold, token.italic);
                    doc.setFont(hasRoboto ? "Roboto" : "helvetica", style);
                    const tokenWidth = doc.getTextWidth(token.text);
                    const exceeds = currentWidth + tokenWidth > maxTextWidth;

                    if (exceeds && currentLine.length > 0 && token.text.trim() !== "") {
                        wrappedLines.push({ tokens: currentLine, width: currentWidth });
                        currentLine = [];
                        currentWidth = 0;
                    }

                    currentLine.push(token);
                    currentWidth += tokenWidth;
                });

                if (currentLine.length > 0) {
                    wrappedLines.push({ tokens: currentLine, width: currentWidth });
                }

                wrappedLines.forEach((wrappedLine, index) => {
                    const isLastLine = index === wrappedLines.length - 1;
                    drawStyledLine(wrappedLine.tokens, wrappedLine.width, !isLastLine);
                });
            });
        };

        const addCenteredImage = (imageData, width, gap = 8) => {
            const height = (imageData.h / imageData.w) * width;
            const x = centerX - width / 2;
            doc.addImage(imageData.data, "PNG", x, cursorY, width, height);
            cursorY += height + gap;
        };

        const logoW = 75;
        const logoH = (logoImg.h / logoImg.w) * logoW;
        doc.addImage(logoImg.data, "PNG", marginX, cursorY, logoW, logoH);
        cursorY += logoH + 20;

        addWrappedLine(certificateDateLine, 11, true, false, 18);
        cursorY += 50;
        addWrappedLine("A QUIEN CORRESPONDA", 13, true, true, 18);
        cursorY += 30;
        addStyledBody(sanitizedCertificateBody, 12, 18, 18);
        addWrappedLine("", 12, false, false, 16);
        addWrappedLine(`Se expide a solicitud del interesado, a los ${issueDateLiteral}.`, 12, false, false, 18);
        addWrappedLine("", 12, false, false, 22);

        const firmaW = 240;
        addCenteredImage(firmaImg, firmaW, 6);

        addWrappedLine("Andres Felipe Rodriguez Rincon", 12, true, true, 16);
        addWrappedLine("CEO de Ashara Studios S.A.S", 11, false, true, 16);
        addWrappedLine("Correo: andresr@asharastudios.com", 11, false, true, 16);
        addWrappedLine("Celular: 3142114475", 11, false, true, 16);

        const logoHW = 140;
        const logoHH = (logoHImg.h / logoHImg.w) * logoHW;
        const footerBottomMargin = 34;
        const footerTextGap = 16;
        const footerLogoY = pageHeight - footerBottomMargin - logoHH - footerTextGap - 12;
        const footerLogoX = centerX - (logoHW / 2);
        doc.addImage(logoHImg.data, "PNG", footerLogoX, footerLogoY, logoHW, logoHH);
        doc.setFont(hasRoboto ? "Roboto" : "helvetica", "bold");
        doc.setFontSize(11);
        doc.text("NIT: 901.071.674-6", centerX, footerLogoY + logoHH + footerTextGap, { align: "center" });

        return doc;
    };

    const handleDownloadCertificate = async () => {
        const doc = await buildCertificatePdf();

        const safeName = employeeName.replace(/\s+/g, "_").toLowerCase();
        doc.save(`certificado_laboral_${safeName}.pdf`);
        setShowCertificatePopup(false);
    };

    return (
        <>
            <button className="profile-certificate-btn" onClick={handleOpenPopup}>Certificado Laboral</button>

            <Popup
                isOpen={showCertificatePopup}
                title="Certificado Laboral"
                onClose={() => setShowCertificatePopup(false)}
                onCancel={() => setShowCertificatePopup(false)}
                onConfirm={handleDownloadCertificate}
                cancelText="Cancelar"
                confirmText="Descargar PDF"
                maxWidth="900px"
                height="90vh"
            >
                <div className="certificate-editor">
                    <textarea
                        id="certificateBody"
                        value={certificateBody}
                        onChange={(event) => setCertificateBody(event.target.value)}
                        rows={6}
                    />
                    <button
                        type="button"
                        className="certificate-refresh-btn"
                        onClick={handleRefreshPreview}
                        disabled={isGeneratingPreview}
                    >
                        {isGeneratingPreview ? "Actualizando..." : "Actualizar documento"}
                    </button>
                </div>

                <div className="certificate-preview">
                    {previewPdfUrl && !isGeneratingPreview ? (
                        <iframe
                            title="Vista previa PDF"
                            src={previewPdfUrl}
                            className="certificate-pdf-preview"
                        ></iframe>
                    ) : (
                        <div className="certificate-preview-loading">Generando vista previa del PDF...</div>
                    )}
                </div>
            </Popup>
        </>
    );
}
